import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetTravellerBearersQueryKey,
  useTravelCardRename,
} from '@/api/trafikportalen';
import { getApiErrorMessage } from '@/helpers/apiError';
import { formatCardNumber } from '@/helpers/formatting';
import { trimData } from '@/helpers/trimData';
import { toast } from '@/hooks/useToast';
import { IconCheck } from '@/icons';
import Button from '../core/Button';
import { FormElement } from '../FormElement';
import { FormSection } from '../FormSection';
import Input from '../core/Input';
import { ReplaceSchoolCardForm } from './ReplaceSchoolCardForm';

interface TravelCardUpdateFormProps {
  travellerId: string;
  serial: string;
  currentLabel?: string;
  isSchoolCard?: boolean;
  onOpenChange: (open: boolean) => void;
  onReplacingChange?: (replacing: boolean) => void;
}

export const TravelCardUpdateForm = ({
  travellerId,
  serial,
  currentLabel,
  isSchoolCard = false,
  onOpenChange,
  onReplacingChange,
}: TravelCardUpdateFormProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const renameCard = useTravelCardRename();

  const [newLabel, setNewLabel] = useState(currentLabel ?? '');
  const [replacing, setReplacing] = useState(false);
  const replaceButtonRef = useRef<HTMLButtonElement>(null);
  const wasReplacing = useRef(false);
  const replacedRef = useRef(false);

  useEffect(() => {
    if (wasReplacing.current && !replacing) {
      replaceButtonRef.current?.focus();
    }
    wasReplacing.current = replacing;
  }, [replacing]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const labelChanged = trimData(newLabel) !== (currentLabel ?? '');

      if (!labelChanged) {
        onOpenChange(false);
        return;
      }

      try {
        await renameCard.mutateAsync({
          data: {
            traveller_id: travellerId,
            serial,
            label: trimData(newLabel),
          },
        });

        toast.success({
          title: formatMessage({
            defaultMessage: 'Kortet uppdaterat',
          }),
        });

        await queryClient.invalidateQueries({
          queryKey: getGetTravellerBearersQueryKey(travellerId),
        });

        onOpenChange(false);
      } catch (error) {
        toast.error({
          title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
          description: getApiErrorMessage(
            error,
            formatMessage({ defaultMessage: 'Ett fel inträffade' }),
          ),
        });
      }
    },
    [
      currentLabel,
      formatMessage,
      newLabel,
      onOpenChange,
      queryClient,
      renameCard,
      serial,
      travellerId,
    ],
  );

  const handleStartReplace = useCallback(() => {
    onReplacingChange?.(true);
    setReplacing(true);
  }, [onReplacingChange]);

  const handleReplaceOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !replacedRef.current) {
        onReplacingChange?.(false);
        setReplacing(false);
      }
    },
    [onReplacingChange],
  );

  const handleReplaced = useCallback(() => {
    replacedRef.current = true;
    onOpenChange(false);
  }, [onOpenChange]);

  if (isSchoolCard && replacing) {
    return (
      <ReplaceSchoolCardForm
        travellerId={travellerId}
        currentSerial={serial}
        onOpenChange={handleReplaceOpenChange}
        onSuccess={handleReplaced}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <FormSection editMode={true}>
          <FormElement
            label={<FormattedMessage defaultMessage="Kortnummer" />}
            input={
              <span className="font-mono text-sm">
                {formatCardNumber(serial)}
              </span>
            }
          />
          <FormElement
            id="label"
            label={<FormattedMessage defaultMessage="Namn på kort" />}
            input={
              <Input
                id="label"
                type="text"
                value={newLabel}
                maxLength={100}
                onChange={(e) => setNewLabel(e.target.value)}
              />
            }
          />
        </FormSection>
        <div className="mt-4 flex gap-2">
          {isSchoolCard && (
            <Button
              ref={replaceButtonRef}
              variant="outline"
              aria-label={formatMessage(
                { defaultMessage: 'Byt skolkort {serial}' },
                { serial: formatCardNumber(serial) },
              )}
              onClick={handleStartReplace}
            >
              <FormattedMessage defaultMessage="Byt skolkort" />
            </Button>
          )}
          <Button
            variant="ghost"
            className="ml-auto"
            onClick={() => onOpenChange(false)}
          >
            <FormattedMessage defaultMessage="Avbryt" />
          </Button>
          <Button
            type="submit"
            variant="filled"
            startIcon={<IconCheck />}
            loading={renameCard.isPending}
          >
            <FormattedMessage defaultMessage="Uppdatera" />
          </Button>
        </div>
      </form>
    </div>
  );
};
